import chalk from "chalk";
import { createContext, type PropsWithChildren, useEffect, useRef } from "react";
import { ProductReferenceCalculationSource } from "@/features/diagnostics/resources/constants.ts";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import ErrorLoadingConfigurator from "@/features/sub-families/components/error-loading-configurator.tsx";
import { PART_SMART_NUMBER_PREFIX } from "@/features/sub-families/resources/constants.ts";
import { getAllInstallations, getInitialInstallationId } from "@/features/sub-families/utils/installations.ts";
import { getPartNumberFromDescriptor } from "@/features/sub-families/utils/parts.ts";
import {
    getPowerSupplyOptionsByProductScenario,
    getProductScenario,
} from "@/features/sub-families/utils/power-supply-dimming-type.ts";
import { getEmptyConfigCode } from "@/features/sub-families/utils/smart-number.ts";
import Translations from "@/features/translations/components/translations.tsx";
import type { Translations as TranslationsType } from "@/features/translations/types";
import Logger from "@/lib/logger.ts";
import { getProductReferenceBySmartNumberMapping, getProductReferenceByUserInput } from "@/lib/product-reference.ts";
import { getAccessoryByConfigCode } from "@/services/product/utils/accessories.ts";
import { getMountingKitByConfigCode } from "@/services/product/utils/mounting-kits.ts";
import { crmPathSetAction } from "@/redux/slices/crm-slice.ts";
import {
    downloadsSetAllowedOptionsAction,
    downloadsSetDynamicSpecSheetPathAction,
    downloadsSetZipDownloadPathAction,
} from "@/redux/slices/downloads-slice.ts";
import {
    filterOptionsSetOptionsAction,
    filterOptionsSetSubFamiliesAction,
} from "@/redux/slices/filter-options-slice.ts";
import { installationsSelectAction, installationsSetOptionsAction } from "@/redux/slices/installations-slice.ts";
import { itemListSetPathsAction } from "@/redux/slices/item-list-slice.ts";
import { modelSetGltfAction } from "@/redux/slices/model-slice.ts";
import { partsSetAction, partsSetAllowAllPartsAction, partsSetAllPartsAction } from "@/redux/slices/parts-slice.ts";
import { productsSetAction } from "@/redux/slices/products-slice.ts";
import {
    smartNumberSetDescriptionAction,
    smartNumberSetDifferentAction,
    smartNumberSetMappingAction,
} from "@/redux/slices/smart-number-slice.ts";
import {
    subFamilySetAction,
    subFamilySetFamilyAction,
    subFamilySetImageAction,
} from "@/redux/slices/sub-family-slice.ts";
import { useAppDispatch, useAppSelector } from "@/redux/store.ts";
import { type Nullable, SmartNumberDescriptor } from "@/types";
import { sortItemsBy } from "@/utils/sort.ts";
import { StringView } from "@/utils/text.ts";
import useInitializedContext from "../../../hooks/use-initialized-context.ts";
import useSubFamiliesConfigFetch from "../hooks/use-sub-families-config-fetch.ts";
import type {
    FilterOptions,
    ModelCode,
    Part,
    ProductData,
    SmartNumber,
    SmartNumberDescription,
    SmartNumberMapping,
    SubFamily,
} from "../types";
import { getFilterOptions } from "../utils/filters.ts";

// region Context
type SubFamiliesConfigContextType = {
    filterOptions: Nullable<FilterOptions>;
    subFamilies: SubFamily[];
    isLoading: boolean;
    isPlaceholderData: boolean;
};

const SubFamiliesConfigContext = createContext<Nullable<SubFamiliesConfigContextType>>(null);

export const useSubFamiliesConfig = () => useInitializedContext(SubFamiliesConfigContext, "SubFamiliesConfigProvider");
// endregion

type Props = PropsWithChildren<{ modelCode: Nullable<ModelCode>; userInput: Nullable<SmartNumber> }>;
export default function SubFamiliesConfigProvider({ children, modelCode, userInput }: Props) {
    const dispatch = useAppDispatch();
    const filterOptions = useAppSelector((state) => state.filterOptions.options);
    const subFamilies = useAppSelector((state) => state.filterOptions.subFamilies);

    const hasFetched = useRef<boolean>(false);

    const { data, isPlaceholderData, isLoading, isError, failureCount, refetch } = useSubFamiliesConfigFetch(modelCode);

    useEffect(() => {
        // Reset this flag whenever user-input has changed BEFORE the fetch effect runs
        hasFetched.current = false;
    }, [userInput]);

    useEffect(() => {
        if (!data) return;

        const installations = getAllInstallations(data.subFamilies);
        const initialInstallationId = getInitialInstallationId(data.id, data.subFamilies, installations);

        const allFilterOptions = getFilterOptions(data.productData);

        const gltfLocation = data.glbUrl;

        const currentSubFamily = data.subFamilies.find(({ id }) => id === data.id);

        const parts = sortItemsBy(data.parts, "partNo");
        const { productData: products, smartNumberMapping: defaultMapping, smartnumberDescription } = data;

        DiagnosticsStore.collectProductInfo(products);

        const args: Args = {
            parts,
            products,
            mapping: defaultMapping,
            description: smartnumberDescription,
            // Only ever check the user input on the initial fetch.
            // Changes of subfamily should not notify about a different smartnumber + user-input combination.
            userInput: !hasFetched.current ? userInput : null,
        };

        const { smartNumberMapping, notifyDifferences } = getSmartNumberMappingDetails(args);

        // Dispatch centralized data first
        dispatch(productsSetAction(products));
        dispatch(partsSetAction(parts));
        dispatch(partsSetAllPartsAction(data.allParts));
        dispatch(partsSetAllowAllPartsAction(data.allowAllParts));

        dispatch(filterOptionsSetOptionsAction(allFilterOptions));
        dispatch(filterOptionsSetSubFamiliesAction(data.subFamilies));

        // Smart number definitions and mapping (= filter state)
        dispatch(smartNumberSetMappingAction(smartNumberMapping));
        dispatch(smartNumberSetDescriptionAction(smartnumberDescription));
        dispatch(smartNumberSetDifferentAction(notifyDifferences));

        dispatch(installationsSetOptionsAction(installations));
        dispatch(installationsSelectAction(initialInstallationId));

        dispatch(downloadsSetAllowedOptionsAction(data.downloadOptions));
        dispatch(downloadsSetDynamicSpecSheetPathAction(data.dynamicSpecsheetPath));
        dispatch(downloadsSetZipDownloadPathAction(data.zipDownloadPath));

        // Dispatch "visible data" that consumes data dispatched above
        dispatch(subFamilySetAction({ id: data.id, name: data.title }));
        dispatch(subFamilySetFamilyAction(data.family));
        dispatch(modelSetGltfAction(gltfLocation));

        dispatch(crmPathSetAction(data.crmFormPath));

        dispatch(
            itemListSetPathsAction({
                wishlistAddPath: data.whistlistAddPath,
                wishlistRemovePath: data.whistlistRemovePath,
                wishlistListPath: data.wishlistListPath,
                pinnedCollectionAddPath: data.pinnedCollectionAddPath,
                pinnedCollectionRemovePath: data.pinnedCollectionRemovePath,
            }),
        );

        // Dispatch data from the current subfamily
        if (currentSubFamily) {
            dispatch(subFamilySetImageAction(currentSubFamily.image));
        }

        hasFetched.current = true;
    }, [data, dispatch, userInput]);

    return (
        <Translations translations={data?.labels ?? ({} as TranslationsType)}>
            <SubFamiliesConfigContext value={{ isLoading, isPlaceholderData, filterOptions, subFamilies }}>
                {isError ? <ErrorLoadingConfigurator canRetry={failureCount < 3} onRetry={refetch} /> : children}
            </SubFamiliesConfigContext>
        </Translations>
    );
}

// region User input algorithm
type Args = { parts: Part[]; products: ProductData; mapping: SmartNumberMapping; description: SmartNumberDescription; userInput: Nullable<string> }; // prettier-ignore
type MappingDetails = { smartNumberMapping: SmartNumberMapping; notifyDifferences: boolean };
function getSmartNumberMappingDetails(args: Args): MappingDetails {
    const { parts, products, mapping, description, userInput } = args;

    // When no user input was found, just return the mapping from the original product
    if (!userInput) return { smartNumberMapping: mapping, notifyDifferences: false }; // No user input -> no notification

    // Create the product reference from the received mapping
    const defaultProductRefence = getProductReferenceBySmartNumberMapping(mapping, description, {
        source: ProductReferenceCalculationSource.SubFamiliesConfigApiMapping,
        products,
    });

    // Let's try to parse a product reference from the user input
    const userInputProductReference = getProductReferenceByUserInput(userInput, description, {
        source: ProductReferenceCalculationSource.SubFamiliesConfigUserInput,
        products,
    });

    // The product references should never differ, the base product should always be found for a given user input
    if (userInputProductReference !== defaultProductRefence) {
        return { smartNumberMapping: mapping, notifyDifferences: true };
    }

    // Now let's see if we find a product for this reference
    const productByUserInput = products[userInputProductReference] ?? null;

    // No product found? We cannot apply anything from the user input top the mapping
    if (!productByUserInput) return { smartNumberMapping: mapping, notifyDifferences: false }; // Do not notify when product was found - the reference will always be correct in case of a valid user input

    const stringView = new StringView(userInput);

    Logger.groupCollapsed("User input parsing");
    DiagnosticsStore.collectEventLog("info", "Parsing user inpput");

    // Clone the mapping so we are sure our changes won't break anything
    const remappedSmartNumberMapping = structuredClone(mapping);

    let notifyDifferences = false; // Do not notify the user by default as the happy path is that no differences are detected in the user input!

    for (const definition of description) {
        const descriptor = definition.CharacteristicName;
        const length = +definition.CharacteristicLength;
        if (Number.isNaN(length)) continue;

        const configCodeCandidate = stringView.destructiveSlice(length);

        // We should notify about differences when the global field was set to true or when the config code could have been valid
        const shouldNotifyAboutDifferences: boolean = notifyDifferences || configCodeCandidate !== "";
        let hasLogged = false;

        Logger.info(`[${chalk.cyan(descriptor)}:${chalk.yellow(configCodeCandidate || "<empty>")}]`);
        DiagnosticsStore.collectEventLog("info", `[User Input] ${descriptor}:${configCodeCandidate || "<empty>"}`);

        switch (descriptor) {
            case SmartNumberDescriptor.Accessory: {
                const emptyConfigCode = getEmptyConfigCode(SmartNumberDescriptor.Accessory, description);
                const candidateIsEmptyConfigCode = emptyConfigCode === configCodeCandidate;

                const accessory = getAccessoryByConfigCode(productByUserInput, configCodeCandidate);

                // Also check against empty config code since this isn't a real option in the data but one provided by React
                if (!accessory && !candidateIsEmptyConfigCode) {
                    notifyDifferences = shouldNotifyAboutDifferences;
                    if (notifyDifferences && !hasLogged) {
                        hasLogged = true;
                    }
                    break;
                }

                remappedSmartNumberMapping[descriptor] =
                    // Safeguard the mapping with the original value - which should always be valid
                    accessory?.code ?? emptyConfigCode ?? remappedSmartNumberMapping[descriptor];

                break;
            }

            case SmartNumberDescriptor.MountingKit: {
                const emptyConfigCode = getEmptyConfigCode(SmartNumberDescriptor.MountingKit, description);
                const candidateIsEmptyConfigCode = emptyConfigCode === configCodeCandidate;

                const mountingKit = getMountingKitByConfigCode(productByUserInput, configCodeCandidate);

                // Also check against empty config code since this isn't a real option in the data but one provided by React
                if (!mountingKit && !candidateIsEmptyConfigCode) {
                    notifyDifferences = shouldNotifyAboutDifferences;
                    if (notifyDifferences && !hasLogged) {
                        hasLogged = true;
                    }
                    break;
                }

                remappedSmartNumberMapping[descriptor] =
                    // Safeguard the mapping with the original value - which should always be valid
                    mountingKit?.configCode ?? emptyConfigCode ?? remappedSmartNumberMapping[descriptor];

                break;
            }

            case SmartNumberDescriptor.PowerSupply: {
                const productScenario = getProductScenario(productByUserInput);
                if (productScenario === null) break; // Should not happen - let's await test results and re-evaluate when needed

                const emptyConfigCode = getEmptyConfigCode(SmartNumberDescriptor.PowerSupply, description);
                const candidateIsEmptyConfigCode = emptyConfigCode === configCodeCandidate;

                const options = getPowerSupplyOptionsByProductScenario(productScenario, productByUserInput);
                const powerSupply = options.find((option) => option.configCode === configCodeCandidate);

                // Also check against empty config code since this isn't a real option in the data but one provided by React
                if (!powerSupply?.configCode && !candidateIsEmptyConfigCode) {
                    notifyDifferences = shouldNotifyAboutDifferences;
                    if (notifyDifferences && !hasLogged) {
                        hasLogged = true;
                    }
                    break;
                }

                remappedSmartNumberMapping[descriptor] =
                    // Safeguard the mapping with the original value - which should always be valid
                    powerSupply?.configCode ?? emptyConfigCode ?? remappedSmartNumberMapping[descriptor];

                break;
            }

            default: {
                // We're only interested in parts if none of the above cases was hit
                if (!descriptor.startsWith(PART_SMART_NUMBER_PREFIX)) break;

                const partNo = getPartNumberFromDescriptor(descriptor);
                if (!partNo) break; // Should not happen

                const currentPart = parts.find((part) => part.partNo === partNo);
                if (!currentPart) break; // Should not happen

                const colour = currentPart.colors.find(({ code }) => code === configCodeCandidate);
                if (!colour) {
                    notifyDifferences = shouldNotifyAboutDifferences;
                    if (notifyDifferences && !hasLogged) {
                        hasLogged = true;
                    }
                    break;
                }

                remappedSmartNumberMapping[descriptor] = colour.code;

                break;
            }
        }
    }

    Logger.groupEnd();

    DiagnosticsStore.collectEventLog("info", `Warning out differences: ${notifyDifferences}`);

    return { smartNumberMapping: remappedSmartNumberMapping, notifyDifferences };
}
// endregion
